import { FilterCategory, IndexedFilters } from '@/catalog/types/filters';
import { FilterOptionCheckbox } from './filter-option-checkbox';
import FilterOptionCheckboxContainer from './filter-option-checkbox-container';
import { useMemo } from 'react';
import { cn } from '@/shadcn-ui/utils';
import isOthersColorFilter from '@/catalog/utils/is-others-color-filter';
import isFilterCheckboxChecked from '@/catalog/utils/is-filter-checkbox-checked';
import getInventoryFilterColors from '@/catalog/utils/get-inventory-filter-colors';
import { getAdjustedColorsFilterCount } from '@/catalog/utils/get-adjusted-colors-filter-count';
import getNumberFromObjectKvp from '@/common/utils/get-number-from-object-kvp';
import { NumberObject } from '@/common/types/number-object';
import { getFormattedFilterLabel } from '@/catalog/utils/get-formatted-filter-label';
import isColorFilter from '@/catalog/utils/is-color-filter';
import { OTHERS_OPTIONS } from '@/catalog/utils/parse-indexed-filter-for-label';
import { useCheckboxToggles } from '@/catalog/hooks/use-checkbox-toggle';

export interface FilterOptionCheckboxGroupProps {
  categoryName: FilterCategory;
  categoryFilters: string[];
  categoryFiltersCount?: NumberObject;
  selectedFilters: string[];
  setSelectedFilters: React.Dispatch<React.SetStateAction<IndexedFilters>>;
}

export default function FilterOptionCheckboxGroup({
  categoryName,
  categoryFilters,
  categoryFiltersCount,
  selectedFilters,
  setSelectedFilters,
}: FilterOptionCheckboxGroupProps) {
  const { toggleCheckbox } = useCheckboxToggles<string>({
    categoryName,
    setSelectedFilters,
  });

  const adjustedCategoryFilters = useMemo(() => {
    if (isColorFilter(categoryName)) {
      const filteredColors = getInventoryFilterColors(categoryFilters);
      return categoryFilters.length !== filteredColors.length &&
        OTHERS_OPTIONS[categoryName]?.predicate !== undefined
        ? filteredColors.concat('Others')
        : filteredColors;
    }
    return categoryFilters;
  }, [categoryFilters, categoryName]);

  const adjustedCounts = useMemo(() => {
    if (isColorFilter(categoryName) && categoryFiltersCount) {
      return getAdjustedColorsFilterCount(categoryFiltersCount);
    }
    return categoryFiltersCount;
  }, [categoryName, categoryFiltersCount]);

  return (
    <FilterOptionCheckboxContainer>
      {adjustedCategoryFilters.map((filter) => {
        const checked = isFilterCheckboxChecked(categoryName, filter, selectedFilters);

        const count = getNumberFromObjectKvp(filter, adjustedCounts);
        const disabled = !checked && count !== undefined && count === 0;
        return (
          <FilterOptionCheckbox
            key={filter}
            categoryName={categoryName}
            label={getFormattedFilterLabel({ categoryName, filter, count, disabled })}
            value={filter}
            checked={checked}
            disabled={disabled}
            onClick={() =>
              toggleCheckbox(
                checked,
                isOthersColorFilter(categoryName, filter)
                  ? getInventoryFilterColors(categoryFilters, false)
                  : filter
              )
            }
            color={isColorFilter(categoryName) ? filter : undefined}
            className={cn({
              'lg:pr-[64px]': categoryName === 'shipping' && filter === 'local_only',
            })}
          />
        );
      })}
    </FilterOptionCheckboxContainer>
  );
}
